import {useState, useCallback, useMemo} from 'react';

/**
 * Custom hook to handle annotation dragging
 */
const useAnnotationDrag = ({ 
  annotation, 
  otherAnnotations, 
  toMusicalPosition, 
  calculatePosition,
  scrollPosition = 0,
  pixelsPerBeat,
  totalWidth,
  beatsPerMeasureMap
}) => {
  const [isDragging, setIsDragging] = useState(false);

  const width = useMemo(() => {
    const startX = calculatePosition(annotation.startMeasure, annotation.startBeat, annotation.startSubdivision || 0);
    const endX = calculatePosition(annotation.endMeasure, annotation.endBeat, annotation.endSubdivision || 0);
    return endX - startX;
  }, [annotation, calculatePosition]);

  const dragBoundFunc = useCallback((pos) => {
    const sorted = [...otherAnnotations, annotation].sort((a, b) => {
      const aX = calculatePosition(a.startMeasure, a.startBeat, a.startSubdivision || 0);
      const bX = calculatePosition(b.startMeasure, b.startBeat, b.startSubdivision || 0);
      return aX - bX;
    });

    const index = sorted.findIndex(a => a.id === annotation.id);
    const prev = sorted[index - 1];
    const next = sorted[index + 1];

    const leftLimit = prev
      ? calculatePosition(prev.endMeasure, prev.endBeat, prev.endSubdivision || 0) - scrollPosition
      : -scrollPosition;

    const rightmostPosition = calculatePosition(beatsPerMeasureMap.length + 1, 1, 1);
    const rightLimit = next
      ? calculatePosition(next.startMeasure, next.startBeat, next.startSubdivision || 0) - scrollPosition - width
      : rightmostPosition - scrollPosition - width;

    return {
      x: Math.min(Math.max(pos.x, leftLimit), rightLimit),
      y: 0
    };
  }, [annotation, otherAnnotations, calculatePosition, width, scrollPosition, beatsPerMeasureMap]);

  return {
    isDragging,
    setIsDragging,
    dragBoundFunc,
    width
  };
};

export default useAnnotationDrag;
